/* eslint-disable semi-spacing */
import Render from "@/pages/dashboard/components/PageContainer/Render";
import React, { ReactNode } from "react";
import CommonBubble from "@home/components/CommonBubble";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { formatPercentNum } from '@/pages/dashboard/utils/utils';
import { resolveAndSelectName, resolveConfigString } from '@/pages/dashboard/utils/metricUtils';
import type { TMetricsInfo } from '@/pages/dashboard/utils/metricUtils';
import type { IExplain } from "@/pages/dashboard/types/dashboard";
import type { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";
import type { IColumn } from "./type";

/**
 * 下载前展开列：为配置了 showChangeRate + changeKey 的列在其后追加 DoD 环比列
 * 英文列名加 _dod，中文列名加 _环比
 */
export const expandColumnsForDownload = (
  columns: IColumn[],
  isEN: boolean,
): IColumn[] => {
  if (!columns?.length) return [];
  const result: IColumn[] = [];
  columns.forEach((col: any) => {
    result.push(col);
    const cellConfig = col?.features?.cell || {};
    const { showChangeRate, changeKey, dodDecimals = 1 } = cellConfig;
    const dodFormat = cellConfig.dodDownLoadFormat ?? cellConfig.dodDownloadFormat ?? col?.features?.dodDownloadFormat ?? col?.features?.dodDownLoadFormat;
    if (showChangeRate && changeKey && col?.code) {
      const mainNameEn = col.nameEn || col.name || col.code;
      const mainNameCn = col.nameCn || col.name || col.code;
      const dodCol: any = {
        code: changeKey,
        name: isEN ? `${mainNameEn}_dod` : `${mainNameCn}_环比`,
        nameEn: `${mainNameEn}_dod`,
        nameCn: `${mainNameCn}_环比`,
        align: col.align ?? 'right',
        width: col.width,
        features: {
          ...col.features,
          downLoadFormat: dodFormat || ((value: any) => {
            if (value === null || value === undefined || value === '') {
              return { t: 's' as const, v: '-', z: '@' };
            }
            const formatted = formatPercentNum(parseFloat(String(value)), dodDecimals, true);
            return { t: 's' as const, v: formatted, z: '@' };
          }),
        },
      };
      result.push(dodCol);
    }
  });
  return result;
};

/**
 * 返回负数表示 a 在 b 前面，如 (a, b)
 * 返回正数表示 a 在 b 前面，如 (b, a)
 * 返回 0 或者 NaN 表示 a 在排序上等于 b
 */
export const defaultCompareFn = (x: any, y: any, desc: boolean): number => {
  const dir = desc ? -1 : 1;
  const isNil = (v: unknown) => v === null || v === undefined || v === '';

  if (isNil(x) && isNil(y)) {
    return 0;
  }

  if (isNil(x)) {
    return 1 * dir;
  }

  if (isNil(y)) {
    return -1 * dir;
  }

  /** 数字类型排序 */
  if (typeof x === 'number' && typeof y === 'number') {
    return x - y;
  }

  /** 布尔型 */
  if (typeof x === 'boolean' && typeof y === 'boolean') {
    return Number(x) - Number(y);
  }

  if (typeof x === 'string' && typeof y === 'string') {
    // 字符串使用 默认的字典序
    if (x < y) return -1;
    if (x > y) return 1;
    return 0;
  }

  if (Array.isArray(x) && Array.isArray(y)) {
    const len = Math.min(x.length, y.length);
    for (let i = 0; i < len; i++) {
      const cmp = defaultCompareFn(x[i], y[i], desc);
      if (cmp !== 0) {
        return cmp;
      }
    }
    // 数组长度不等时，元素少的字段放在前面
    return x.length - y.length;
  }

  // 对于不认识的数据类型，返回 0
  return 0;
};

/**
 * * 注意区分 - CommonTable 这边使用的 ali-react-table [{ code: string, order: 'desc' | 'asc' | 'none' }]
 * * 注意区分 - SortPopover 那边使用的 [{ by: string, order: 'desc' | 'asc' | 'undefined' }]
 * * 注意区分 - 后端接受的是 [{ sort_by: string, order: boolean }]
*/
export const convertSortFormat = (input: any[] = [], target: 'ali' | 'popover' | 'backend', options?: { revertSort: boolean; }): any[] => {
  if (!Array.isArray(input) || input.length === 0) return [];
  if (!('code' in input[0]) && !('by' in input[0]) && !('sort_by' in input[0])) return [];

  // 通用 normalize 函数
  const normalize = (item: any) => {
    const key = item.code ?? item.by ?? item.sort_by;
    let order = item.order ?? item.desc;

    // 后端类型特殊处理
    if (typeof order === 'boolean') {
      order = order ? 'desc' : 'asc';
    }
    if (order === 'none' || order === undefined || order === null) {
      order = 'none';
    }

    return { key, order };
  };

  let normalized = input.map(normalize);
  // 反转排序方向
  if (options?.revertSort) {
    normalized = normalized.map((n) => ({
      ...n,
      order: n.order === 'asc' ? 'desc' : (n.order === 'desc' ? 'asc' : n.order),
    }));
  }

  // 转换输出格式
  switch (target) {
    case 'ali':
      return normalized.map((n) => ({
        code: n.key,
        order: n.order as 'asc' | 'desc' | 'none',
      }));

    case 'popover':
      return normalized.map((n) => ({
        by: n.key,
        order: n.order === 'none' ? undefined : n.order,
      }));

    case 'backend':
      return normalized
        .filter((n) => n.order !== 'none')
        .map((n) => ({
          sort_by: n.key,
          desc: n.order === 'desc',
        }));

    default:
      return [];
  }
};

export const sortAdapter = {
  toAli: (input: any[]) => convertSortFormat(input, 'ali'),
  toPopover: (input: any[]) => convertSortFormat(input, 'popover'),
  toBackend: (input: any[]) => convertSortFormat(input, 'backend'),
};

/**
 * 根据 tableMetric 配置筛选并排序指标列表（类似 OverallTableCo）
 * @param metrics 原始指标配置数组
 * @param tableMetric 需要筛选的指标 key 数组（决定顺序）
 */
export const filterComponentMetricsByTableMetric = (
  metrics: ComponentMetricsItemType[],
  tableMetric?: string[],
): ComponentMetricsItemType[] => {
  if (!tableMetric || tableMetric.length === 0) return metrics || [];
  const metricsMap = new Map<string, ComponentMetricsItemType>();
  (metrics || []).forEach((metric) => {
    const key = (metric as any).reqMetricKey || (metric as any).metricKey;
    if (key) metricsMap.set(key, metric);
  });
  return tableMetric
    .map((key) => metricsMap.get(key))
    .filter((m): m is ComponentMetricsItemType => m !== undefined);
};

/**
 * 将 componentMetrics 转换为 CommonTable 的 IColumn 配置（类似 OverallTableCo）
 * 支持格式化、中英文、指标注释（rainbowKey）
 */
export const metricsToColumns = (
  metrics: ComponentMetricsItemType[],
  isEN: boolean,
  options?: {
    explainInfo?: Record<string, { value_en?: string; value_cn?: string; }>;
    metricsInfo?: TMetricsInfo;
  },
): IColumn[] => {
  if (!metrics?.length) return [];
  const { explainInfo, metricsInfo } = options || {};
  const locale = isEN ? 'en-US' : 'zh-CN';
  return metrics.map((m) => {
    const metric = m as any;
    const code = metric.reqMetricKey || metric.metricKey;
    const nameEn = resolveAndSelectName(true, metric.enName, metric.cnName, metricsInfo, undefined, '') || code;
    const nameCn = resolveAndSelectName(false, metric.enName, metric.cnName, metricsInfo, undefined, '') || code;
    const name = resolveAndSelectName(isEN, metric.enName, metric.cnName, metricsInfo, undefined, '') || code;
    const formatRule = metric.formatRule
      ? resolveConfigString(metric.formatRule, metricsInfo, metric.formatRule)
      : metric.formatRule;
    const metricFeatures = metric.features || {};
    const width = isEN ? (metric.enWidth ?? metric.enPercentWidth ?? 140) : (metric.cnWidth ?? metric.cnPercentWidth ?? 120);
    const explain = getExplainVal((explainInfo || {}) as IExplain, metric.rainbowKey || '', locale, metric.tooltipEn, metric.tooltipCn) || '';
    const col: IColumn = {
      code,
      name: name || code,
      nameEn: nameEn || code,
      nameCn: nameCn || code,
      align: metric.align ?? 'right',
      width,
      features: {
        ...metricFeatures,
        format: formatRule,
      },
    };
    if (explain) {
      col.renderHeader = () => (
        <CommonBubble placement="top" content={explain}>
          <span style={{ cursor: 'help' }}>{name}</span>
        </CommonBubble>
      );
    }
    return col;
  });
};

/**
 * 通用插槽渲染函数
 * @param content - 插槽内容，可以是 pageConfig 数组或 React 组件
 * @param className - 包裹容器的类名
 * @param keyPrefix - 用于生成 key 的前缀（可选）
 * @returns 渲染的插槽内容或 null
 */
export const renderSlot = (
  content: React.ReactNode | undefined,
  className?: string,
  keyPrefix?: string,
  restParams?: Object,
): ReactNode => {
  if (!content) return null;

  return (
    <div className={className}>
      {
        // 如果 content 是数组，表示它是从 pageConfig 中传入的
        Array.isArray(content)
          // 遍历数组，渲染每个配置项
          ? content.map((item, index: number) => (
            <Render
              key={keyPrefix ? `${keyPrefix}_${item.name}_${index}` : `${item.name}_${index}`}
              config={item}
              {...restParams}
            />
          ))
          // 表示 content 是从其他 TSX 文件中传入，是传统组件，直接渲染
          : content
      }
    </div>
  );
};
